import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button, Card, CardContent, CardHeader } from '@/components/ui';
import { buildSupportUrl } from '@/utils/supportUrl';

export interface SetupSupportNudgeCardProps {
  academyId?: string;
  onDismiss: () => void | Promise<void>;
  dismissing?: boolean;
  /** Reuse the checklist anchor so the card occupies the same dashboard slot. */
  tourAnchorId?: string;
}

export function SetupSupportNudgeCard({
  academyId,
  onDismiss,
  dismissing = false,
  tourAnchorId,
}: SetupSupportNudgeCardProps) {
  const supportHref = buildSupportUrl({
    type: 'general',
    subject: 'Help getting started',
    academyId,
  });

  return (
    <Card
      id={tourAnchorId}
      className="mb-4 border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/20"
    >
      <CardHeader className="space-y-0 p-3 pb-2 sm:p-4 sm:pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold leading-tight text-gray-900 dark:text-white">
                You have completed all tasks
              </h2>
              <p className="text-xs leading-snug text-gray-500 dark:text-gray-400">
                If you need help finishing setup, don&apos;t hesitate to contact us.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            onClick={() => void onDismiss()}
            disabled={dismissing}
            aria-label="Hide support suggestion"
          >
            {dismissing ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
                …
              </>
            ) : (
              'Hide'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 sm:p-4 sm:pt-0">
        <Button
          asChild
          size="sm"
          variant="outline"
          className="border-emerald-300 bg-white/80 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-gray-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
        >
          <Link to={supportHref}>Submit a support request</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
